import { Box, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import { Types } from '../../enums/TypeEnums';
import {
  ICountry,
  IStock,
  ICurrency,
  ISector,
  useCountriesQuery,
  useCurrenciesQuery,
  useSectorsQuery,
  useStocksFilteredLazyQuery,
  useStockCreatedSubscription,
  useStockUpdatedSubscription
} from '../../generated/graphql';
import { canUserCreate } from '../../utils/rightUtils';
import { FormatTableData } from '../../utils/styleUtils';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import StockForm from './StockForm';
import HistoryUpload from './HistoryUpload';
import remove from '../../assets/images/actions/trash.svg';
import DeleteHSDataForm from './DeleteHSDataForm';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';

const StockList: React.FunctionComponent = () => {
  const history = useNavigate();
  const { state } = useContext(AppContext);

  // Filter states
  const [name, setName] = useState<string>('');
  const [isin, setIsin] = useState<string>('');
  const [exchangeCode, setExchangeCode] = useState<string>('');
  const [country, setCountry] = useState<ICountry | null>(null);
  const [currency, setCurrency] = useState<ICurrency | null>(null);
  const [sector, setSector] = useState<ISector | null>(null);
  const [tradingDate, setTradingDate] = useState<Date | null>(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // Sorting states
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');

  // Local state for displayed stocks (updated by query and subscriptions)
  const [displayedStocks, setDisplayedStocks] = useState<IStock[]>([]);

  const [openUpload, setOpenUpload] = useState(false);
  const [openDeleteHSDataForm, setOpenDeleteHSDataForm] = useState(false);
  const [selectedStock, setSelectedStock] = useState<IStock | null>(null);
  const [openStockForm, setOpenStockForm] = useState<boolean>(false);
  const canCreate = canUserCreate(state.user, Types.Stock);

  const countriesQuery = useCountriesQuery();
  const currenciesQuery = useCurrenciesQuery();
  const sectorsQuery = useSectorsQuery();
  const [fetchStocks, { data, loading }] = useStocksFilteredLazyQuery();

  // Subscriptions for real-time updates
  const createStockSubscription = useStockCreatedSubscription();
  const updateStockSubscription = useStockUpdatedSubscription();

  // Refs for tracking state
  const hasMounted = useRef(false);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFilterChangeRef = useRef(false);
  const isInitialMount = useRef(true);
  const lastPageRef = useRef(page);
  const lastPageSizeRef = useRef(pageSize);

  // Set hasMounted immediately on mount
  useEffect(() => {
    hasMounted.current = true;
  }, []);

  // Helper function to check if a stock matches current filters
  const stockMatchesFilters = useCallback(
    (stock: IStock): boolean => {
      // Check name filter
      if (name && name.trim().length > 0) {
        const stockName = stock.name?.toLowerCase() || '';
        if (!stockName.includes(name.trim().toLowerCase())) {
          return false;
        }
      }

      // Check ISIN filter
      if (isin && isin.trim().length > 0) {
        const stockIsin = stock.isin_code?.toLowerCase() || '';
        if (!stockIsin.includes(isin.trim().toLowerCase())) {
          return false;
        }
      }

      // Check exchange code filter
      if (exchangeCode && exchangeCode.trim().length > 0) {
        const stockExchangeCode = stock.exchange_code?.toLowerCase() || '';
        if (!stockExchangeCode.includes(exchangeCode.trim().toLowerCase())) {
          return false;
        }
      }

      // Check country filter
      if (country && country.id != null) {
        if (stock.country?.id !== country.id) {
          return false;
        }
      }

      // Check currency filter
      if (currency && currency.id != null) {
        if (stock.currency?.id !== currency.id) {
          return false;
        }
      }

      // Check sector filter
      if (sector && sector.id != null) {
        if (stock.sector?.id !== sector.id) {
          return false;
        }
      }

      // Note: trading_date is handled server-side for historyStock loading
      // We don't need to filter client-side for it

      return true;
    },
    [name, isin, exchangeCode, country, currency, sector]
  );

  useEffect(() => {
    if (data?.result) {
      const newTotal = data.result.total || 0;
      setTotal(newTotal);
      // Filter out null/undefined values from the stocks array
      const validStocks: IStock[] = (data.result.stocks || []).filter(
        (stock): stock is IStock => stock !== null && stock !== undefined
      );
      setDisplayedStocks(validStocks);

      // Only reset page if it's actually invalid (only when filters change and we get fewer results)
      if (newTotal > 0) {
        const maxPage = Math.max(0, Math.ceil(newTotal / pageSize) - 1);
        if (page > maxPage && isFilterChangeRef.current) {
          setPage(0);
        }
      } else if (newTotal === 0 && page > 0 && isFilterChangeRef.current) {
        setPage(0);
      }
    }
  }, [data, pageSize, page]);

  // Initial load on mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const filter: any = {
        limit: pageSize,
        offset: 0
      };

      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      fetchStocks({
        variables: { filter }
      });
    }
  }, []);

  // Debounced filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    isFilterChangeRef.current = true;
    filterTimeoutRef.current = setTimeout(() => {
      // Build filter object
      const filter: any = {
        limit: pageSize,
        offset: 0
      };

      if (name && name.trim().length > 0) filter.name = name.trim();
      if (isin && isin.trim().length > 0) filter.isin_code = isin.trim();
      if (exchangeCode && exchangeCode.trim().length > 0)
        filter.exchange_code = exchangeCode.trim();
      if (country && country.id != null) {
        filter.country_id = parseInt(country.id);
      }
      if (currency && currency.id != null) {
        filter.currency_id = parseInt(currency.id);
      }
      if (sector && sector.id != null) {
        filter.sector_id = parseInt(sector.id);
      }
      if (tradingDate) {
        // Format trading date for server (subtract 1 day as per original logic)
        const filterDate = new Date(tradingDate);
        filterDate.setDate(filterDate.getDate() - 1);
        filter.trading_date = filterDate.toISOString().slice(0, 10) + ' 00:00:00';
      }

      // Add sorting parameters
      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      fetchStocks({
        variables: { filter }
      });

      // Reset flag after a short delay
      setTimeout(() => {
        isFilterChangeRef.current = false;
      }, 100);
    }, 500); // 500ms debounce

    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [
    name,
    isin,
    exchangeCode,
    country,
    currency,
    sector,
    tradingDate,
    pageSize,
    orderBy,
    orderDirection,
    fetchStocks
  ]);

  // Reload when sorting changes
  const lastOrderByRef = useRef(orderBy);
  const lastOrderDirectionRef = useRef(orderDirection);

  useEffect(() => {
    // Skip if this is the initial mount or if values haven't changed
    if (isInitialMount.current) {
      lastOrderByRef.current = orderBy;
      lastOrderDirectionRef.current = orderDirection;
      return;
    }

    if (lastOrderByRef.current === orderBy && lastOrderDirectionRef.current === orderDirection) {
      return;
    }

    lastOrderByRef.current = orderBy;
    lastOrderDirectionRef.current = orderDirection;

    const filter: any = {
      limit: pageSize,
      offset: page * pageSize
    };

    if (name && name.trim().length > 0) filter.name = name.trim();
    if (isin && isin.trim().length > 0) filter.isin_code = isin.trim();
    if (exchangeCode && exchangeCode.trim().length > 0) filter.exchange_code = exchangeCode.trim();
    if (country && country.id != null) {
      filter.country_id = parseInt(country.id);
    }
    if (currency && currency.id != null) {
      filter.currency_id = parseInt(currency.id);
    }
    if (sector && sector.id != null) {
      filter.sector_id = parseInt(sector.id);
    }
    if (tradingDate) {
      const filterDate = new Date(tradingDate);
      filterDate.setDate(filterDate.getDate() - 1);
      filter.trading_date = filterDate.toISOString().slice(0, 10) + ' 00:00:00';
    }

    if (orderBy) {
      filter.orderBy = orderBy;
      filter.orderDirection = orderDirection;
    }

    fetchStocks({
      variables: { filter }
    });
  }, [
    orderBy,
    orderDirection,
    page,
    pageSize,
    name,
    isin,
    exchangeCode,
    country,
    currency,
    sector,
    tradingDate,
    fetchStocks
  ]);

  // Reload when pagination changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
      return;
    }

    const isPageChange = lastPageRef.current !== page;
    const isPageSizeChange = lastPageSizeRef.current !== pageSize;

    if (isFilterChangeRef.current && page === 0 && !isPageSizeChange) {
      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
      return;
    }

    if (isPageChange || isPageSizeChange) {
      const filter: any = {
        limit: pageSize,
        offset: page * pageSize
      };

      if (name && name.trim().length > 0) filter.name = name.trim();
      if (isin && isin.trim().length > 0) filter.isin_code = isin.trim();
      if (exchangeCode && exchangeCode.trim().length > 0)
        filter.exchange_code = exchangeCode.trim();
      if (country && country.id != null) {
        filter.country_id = parseInt(country.id);
      }
      if (currency && currency.id != null) {
        filter.currency_id = parseInt(currency.id);
      }
      if (sector && sector.id != null) {
        filter.sector_id = parseInt(sector.id);
      }
      if (tradingDate) {
        const filterDate = new Date(tradingDate);
        filterDate.setDate(filterDate.getDate() - 1);
        filter.trading_date = filterDate.toISOString().slice(0, 10) + ' 00:00:00';
      }

      if (orderBy) {
        filter.orderBy = orderBy;
        filter.orderDirection = orderDirection;
      }

      fetchStocks({
        variables: { filter }
      });

      lastPageRef.current = page;
      lastPageSizeRef.current = pageSize;
    }
  }, [
    page,
    pageSize,
    name,
    isin,
    exchangeCode,
    country,
    currency,
    sector,
    tradingDate,
    orderBy,
    orderDirection,
    fetchStocks
  ]);

  // Handle subscription updates
  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (createStockSubscription.data?.result) {
      const newStock = createStockSubscription.data.result as IStock;

      // Check if new stock matches current filters
      if (stockMatchesFilters(newStock)) {
        setDisplayedStocks((prev) => {
          const exists = prev.some((s) => s.id === newStock.id);
          if (!exists) {
            setTotal((currentTotal) => currentTotal + 1);
            return [newStock, ...prev];
          }
          return prev;
        });
        lastPageRef.current = 0;
        setPage(0);
      }
    }
  }, [createStockSubscription.data, stockMatchesFilters]);

  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (updateStockSubscription.data?.result) {
      const updatedStock = updateStockSubscription.data.result as IStock;
      setDisplayedStocks((prev) => {
        return prev.map((s) => {
          if (s.id === updatedStock.id) {
            // Preserve historyStock if it exists
            const stockWithHistory = {
              ...updatedStock,
              historyStock: s.historyStock
            };
            return stockWithHistory;
          }
          return s;
        });
      });
    }
  }, [updateStockSubscription.data]);


  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortChange = (newOrderBy: string | null, newOrderDirection: 'asc' | 'desc') => {
    setOrderBy(newOrderBy);
    setOrderDirection(newOrderDirection);
    setPage(0);
  };

  const resetFilters = () => {
    setName('');
    setIsin('');
    setExchangeCode('');
    setCountry(null);
    setCurrency(null);
    setSector(null);
    setTradingDate(null);
    setPage(0);
  };

  const renderDeleteHSdata = (rowData: IStock) => {
    return (
      <Button
        id={rowData.id}
        sx={{ width: 'auto' }}
        onClick={() => {
          setOpenDeleteHSDataForm(true);
          setSelectedStock(rowData);
        }}
      >
        <img src={remove} alt="removeButton" />
      </Button>
    );
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 63
    },
    {
      title: 'Stock name',
      field: 'name',
      width: 600
    },
    {
      title: 'Country_code',
      field: 'country.code',
      width: 50
    },
    {
      title: 'Sector classification',
      field: 'sector.name',
      width: 50
    },
    {
      title: 'Close price',
      field: 'historyStock.close_price',
      type: 'numeric',
      width: 104,
      sorting: false
    },
    {
      title: 'Engl Mkt Cap',
      field: 'historyStock.engl_mkt_cap',
      type: 'numeric',
      width: 126,
      sorting: false
    },
    {
      title: 'EPRA Shares',
      width: 121,
      field: 'historyStock.epra_shares',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Price Discount',
      width: 50,
      field: 'historyStock.price_discount',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'ISIN',
      field: 'isin_code',
      width: 155
    },
    {
      title: 'Bloomberg',
      width: 150,
      field: 'bloomberg_ticker'
    },
    {
      title: 'Exchange Code',
      field: 'exchange_code',
      width: 139
    },
    {
      title: 'Currency',
      field: 'currency.code',
      width: 110
    },
    {
      title: 'Delete HS Data',
      width: 30,
      sorting: false,
      render: renderDeleteHSdata
    }
  ] as IHeadCell[];

  const handleDetails = (rowData: IStock) => {
    history(`/stocks/${rowData.id}`, { state: { ...rowData } });
  };

  const countries = countriesQuery.data?.result || [];
  const currencies = currenciesQuery.data?.result || [];
  const sectors = sectorsQuery.data?.result || [];

  return (
    <Box className="overviewPage">
      <OverviewTable
        columns={columns}
        data={FormatTableData(displayedStocks)}
        loading={
          loading || countriesQuery.loading || currenciesQuery.loading || sectorsQuery.loading
        }
        handleDetails={handleDetails}
        handleRemove={() => true}
        hideSearch={true}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        totalCount={total}
        orderBy={orderBy}
        orderDirection={orderDirection}
        onSortChange={handleSortChange}
        createButtonProps={{
          id: 'create-stock-button',
          onClick: () => setOpenStockForm(true),
          text: 'Create stock',
          disabled: !canCreate
        }}
        importButtonProps={{
          id: 'import-stock-data',
          onClick: () => setOpenUpload(true)
        }}
        additionalFilters={
          <Box id="filters" sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <InputComponent
              id="stock-name-filter"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <InputComponent
              id="stock-isin-filter"
              label="ISIN"
              value={isin}
              onChange={(e) => setIsin(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <InputComponent
              id="stock-exchange-code-filter"
              label="Exchange Code"
              value={exchangeCode}
              onChange={(e) => setExchangeCode(e.target.value)}
              additionalStyle={{ width: '200px' }}
            />
            <AutocompleteComponent
              id="stock-country-filter"
              label="Country"
              values={country}
              options={countries}
              onChange={(_: unknown, newValue: ICountry | null) => setCountry(newValue)}
              getOptionLabel={(option: ICountry) => option?.name || ''}
              isSingle={true}
              additionalStyle={{ width: '200px' }}
            />
            <AutocompleteComponent
              id="stock-currency-filter"
              label="Currency"
              values={currency}
              options={currencies}
              onChange={(_: unknown, newValue: ICurrency | null) => setCurrency(newValue)}
              getOptionLabel={(option: ICurrency) => option?.code || ''}
              isSingle={true}
              additionalStyle={{ width: '200px' }}
            />
            <AutocompleteComponent
              id="stock-sector-filter"
              label="Sector"
              values={sector}
              options={sectors}
              onChange={(_: unknown, newValue: ISector | null) => setSector(newValue)}
              getOptionLabel={(option: ISector) => option?.name || ''}
              isSingle={true}
              additionalStyle={{ width: '200px' }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="stock-trading-date-filter"
                label="Trading Date"
                value={tradingDate}
                clearable={true}
                onChange={(value) => setTradingDate(value)}
                format={'dd/MM/yyyy'}
                sx={{ width: '200px' }}
              />
            </LocalizationProvider>
            <CancelButton
              id="clear-filter-button"
              data-testid="clear-filter-button"
              onClick={resetFilters}
              text="clear all"
              sx={{
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                  background: (theme) => theme.palette.primary.main,
                  color: '#fff',
                  border: 'none'
                }
              }}
            />
          </Box>
        }
      />
      {openStockForm && <StockForm handleClose={() => setOpenStockForm(false)} />}
      {openUpload && <HistoryUpload handleClose={() => setOpenUpload(false)} isIndexFile={false} />}
      {openDeleteHSDataForm && (
        <DeleteHSDataForm
          handleClose={() => {
            setOpenDeleteHSDataForm(false);
            setSelectedStock(null);
          }}
          selectedStock={selectedStock}
        />
      )}
    </Box>
  );
};

export default StockList;
